import { useI18n } from '@ecom/i18n';
import { Button, LanguageSwitcher, useTheme } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { useLocale } from '@/lib/locale';
import { useSession } from '@/lib/session';

/**
 * Language, theme and sign-out. In the Topbar from `md` up; below it they move to the foot of the sidebar drawer,
 * so a phone's top bar stays one row.
 */
export function ShellControls({ className }: { className?: string }): ReactElement {
  // Not destructured — `themeState.toggleTheme` rather than a bare
  // `toggleTheme`, the identical avoidance merchant-dashboard's own
  // Topbar/CommandPalette already note (a destructured method trips
  // `@typescript-eslint/unbound-method`).
  const themeState = useTheme();
  const { clearSession } = useSession();
  const { locale, setLocale } = useLocale();
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <LanguageSwitcher
        locale={locale}
        onChange={(next) => {
          setLocale(next);
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          themeState.toggleTheme();
        }}
        aria-label={t('merchant.topbar.themeToggleAria')}
      >
        {themeState.theme === 'dark'
          ? t('merchant.topbar.lightMode')
          : t('merchant.topbar.darkMode')}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          clearSession();
          void navigate('/login', { replace: true });
        }}
      >
        {t('merchant.topbar.logout')}
      </Button>
    </div>
  );
}

/**
 * No search palette, no shop switcher (merchant-dashboard's own Topbar
 * carries both) — this app has two real screens and no shop concept at
 * all. Theme toggle + sign-out only; below `md`, the menu button and the
 * brand instead (the controls live in the drawer there).
 */
export function Topbar({
  menuOpen,
  onOpenMenu,
}: {
  menuOpen: boolean;
  onOpenMenu: () => void;
}): ReactElement {
  const { t } = useI18n();

  return (
    <header className="flex items-center justify-between gap-2 border-b border-line bg-surface px-3 py-2.5 md:px-5 md:py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label={t('merchant.topbar.menu')}
          aria-expanded={menuOpen}
          aria-controls="admin-sidebar"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-line text-inkSoft hover:bg-surfaceHover hover:text-ink md:hidden"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-white md:hidden">
          <img
            src={`${import.meta.env.BASE_URL}icon-192.png`}
            alt=""
            className="h-full w-full object-contain"
          />
        </span>
        <span className="truncate font-display text-base font-bold text-ink md:hidden">
          {t('merchant.sidebar.brand')}
        </span>
        <span className="hidden truncate text-sm font-medium text-inkFaint md:inline">
          {t('admin.topbar.label')}
        </span>
      </div>

      <ShellControls className="hidden justify-end md:flex" />
    </header>
  );
}
